import { ApolloProvider } from '@apollo/client'
import { OFREPWebProvider } from '@openfeature/ofrep-web-provider'
import { OpenFeature } from '@openfeature/web-sdk'
import PartnerBoundary from 'components/boundaries/PartnerBoundary'
import ErrorBoundary from 'lib/boundaries/ErrorBoundary'
import { PosthogManagerProvider } from 'lib/hooks/providers/PosthogManagerProvider'
import MaintenanceMode from 'lib/layouts/MaintenanceMode'
import Root from 'lib/layouts/RootLayout'
import 'react-responsive-modal/styles.css'
import 'utils/css/reset.css'
import 'material-symbols/outlined.css'
import SegmentScript from 'lib/utils/SegmentScript'
import Seo from 'lib/utils/Seo'
import Zendesk from 'lib/utils/Zendesk'
import type { AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import Router from 'next/router'
import setPageName from 'utils/analytics'

import CompanyAndInvoiceProvider from '@/hooks/providers/CompanyAndInvoiceProvider'
import { PostHogProvider } from '@/hooks/providers/PosthogProvider'
import { paymentsClient } from '@/utils/apollo'

const Toast = dynamic(() => import('lib/ui/Toast'), {
  ssr: false
})

declare global {
  interface Window {
    analytics: any
  }
}

// Track client-side page views with Segment
Router.events.on('routeChangeComplete', () => {
  window?.analytics?.page(
    setPageName({ route: Router.route, app: 'Payments Portal' })
  )
})

OpenFeature.setContext({
  /* Its required to send a targeting key to the provider, so before we have the partnerID we are sending a generic ID */
  targetingKey: '999999'
})

OpenFeature.setProvider(
  new OFREPWebProvider({
    // eslint-disable-next-line alternative/no-invalid-env-usage
    baseUrl: process.env.NEXT_PUBLIC_SYSTEM_FEATURE_FLAG_URL,
    pollInterval: 600000, // 10min
    headers: [
      [
        'Authorization',
        // eslint-disable-next-line alternative/no-invalid-env-usage
        `Bearer ${process.env.NEXT_PUBLIC_SYSTEM_FEATURE_FLAG_KEY}`
      ]
    ]
  })
)

function PaymentsPortal({ Component, pageProps }: AppProps) {
  const segmentKey = process.env.NEXT_PUBLIC_SEGMENT_KEY

  const enableMaintenance = process.env.NEXT_PUBLIC_MAINTENANCE_MODE === 'true'

  return (
    <ApolloProvider client={paymentsClient}>
      <PostHogProvider>
        <PosthogManagerProvider>
          {enableMaintenance ? (
            <Root>
              <MaintenanceMode />
            </Root>
          ) : (
            <CompanyAndInvoiceProvider>
              <Seo />
              <SegmentScript app='Payments Portal' segmentKey={segmentKey} />
              <Toast />
              <Root>
                <ErrorBoundary>
                  <PartnerBoundary>
                    <Component {...pageProps} />
                  </PartnerBoundary>
                </ErrorBoundary>
              </Root>
            </CompanyAndInvoiceProvider>
          )}
          <Zendesk />
        </PosthogManagerProvider>
      </PostHogProvider>
    </ApolloProvider>
  )
}

export default PaymentsPortal
